import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, Building2, CheckSquare, Square, Phone } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { shouldSuppressRateLimit } from '../utils/httpErrors';

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmationBanner, setShowConfirmationBanner] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState('');
  const { login, register } = useAuth();
  const navigate = useNavigate();

  // Form state per login
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  // Form state per registrazione
  const [registerData, setRegisterData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    mobilePhone: '',
    federappaltiMembership: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptedTerms: false,
    acceptedPrivacy: false,
    newsletterOptIn: false
  });

  const validateEmail = (email) => {
    const v = (email || '').trim();
    // Semplice check (anti typo), backend valida comunque con EmailStr
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  };

  const validatePasswordRules = (password) => {
    const v = password || '';
    if (v.length < 8) return 'La password deve essere almeno 8 caratteri';
    if (!/[A-Z]/.test(v)) return 'La password deve contenere almeno una lettera maiuscola';
    if (!/[a-z]/.test(v)) return 'La password deve contenere almeno una lettera minuscola';
    if (!/[0-9]/.test(v)) return 'La password deve contenere almeno un numero';
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(v)) return 'La password deve contenere almeno un carattere speciale (!@#$%^&*(),.?":{}|<>)';
    return null;
  };

  const handleLoginChange = (e) => {
    setLoginData({
      ...loginData,
      [e.target.name]: e.target.value
    });
  };

  const handleRegisterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setRegisterData({
      ...registerData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await login(loginData.email, loginData.password);
      if (result.success) {
        toast.success('✅ Login effettuato con successo!');
        navigate('/dashboard');
      } else {
        if (result.silent || shouldSuppressRateLimit(result.error)) {
          return;
        }
        toast.error(result.error || 'Errore durante il login');
      }
    } catch (error) {
      toast.error('Si è verificato un errore. Riprova.');
    } finally {
      setIsLoading(false);
    }
  };
  

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Validazioni
    if (!validateEmail(registerData.email)) {
      toast.error('Inserisci un indirizzo email valido');
      setIsLoading(false);
      return;
    }

    const pwdError = validatePasswordRules(registerData.password);
    if (pwdError) {
      toast.error(pwdError);
      setIsLoading(false);
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Le password non coincidono');
      setIsLoading(false);
      return;
    }

    if (!registerData.acceptedTerms) {
      toast.error('Devi accettare i Termini e Condizioni');
      setIsLoading(false);
      return;
    }

    if (!registerData.acceptedPrivacy) {
      toast.error('Devi accettare la Privacy Policy');
      setIsLoading(false);
      return;
    }

    if (!registerData.mobilePhone.trim()) {
      toast.error('Inserisci il numero di telefono mobile');
      setIsLoading(false);
      return;
    }

    if (!registerData.federappaltiMembership) {
      toast.error('Seleziona una delle opzioni DS Consulting');
      setIsLoading(false);
      return;
    }

  try {
    const result = await register({
      email: registerData.email,
      password: registerData.password,
      first_name: registerData.firstName,
      last_name: registerData.lastName,
      company_name: registerData.companyName || null,
      mobile_phone: registerData.mobilePhone,
      federappalti_membership: registerData.federappaltiMembership,
      accepted_terms: registerData.acceptedTerms,
      accepted_privacy: registerData.acceptedPrivacy,
      newsletter_opt_in: registerData.newsletterOptIn,
    });
  
    if (!result.success) {
     if (shouldSuppressRateLimit(result.error)) {
      return;
     }
     const detail = result.error?.response?.data?.detail;
  
      const formatFastApiDetail = (d) => {
        if (!d) return 'Errore durante la registrazione';
        if (typeof d === 'string') return d;
        if (Array.isArray(d)) {
          // FastAPI validation errors
          return d
            .map((e) => {
              const field = Array.isArray(e.loc) ? e.loc[e.loc.length - 1] : 'field';
              return `${field}: ${e.msg}`;
            })
            .join(' | ');
        }
        // qualsiasi oggetto
        try { return JSON.stringify(d); } catch { return 'Errore durante la registrazione'; }
    };
    
      toast.error(formatFastApiDetail(detail));
      return;
    }
  
    // Mostra banner di conferma email
    setConfirmationEmail(registerData.email);
    setShowConfirmationBanner(true);
    toast.success("✅ Registrazione completata! Controlla la tua email per confermare l'account.");
  
    // Reset form e passa a login
    setRegisterData({
      firstName: '',
      lastName: '',
      companyName: '',
      mobilePhone: '',
      federappaltiMembership: '',
      email: '',
      password: '',
      confirmPassword: '',
      acceptedTerms: false,
      acceptedPrivacy: false,
      newsletterOptIn: false
    });
    setIsLogin(true);
  
  } catch (error) {
    console.error('Errore registrazione:', error);
    toast.error('Errore durante la registrazione');
  } finally {
    setIsLoading(false);
  }

  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL || ''}/sfondo-login.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Overlay per migliorare la leggibilità del contenuto */}
      <div className="absolute inset-0 bg-white/40"></div>
      
      <div className="w-full relative z-10">
  {/* HEADER: centrato sul viewport */}
  <div className="w-full text-center mb-10 px-4">
    <div className="inline-flex items-center justify-center mb-6">
      <img
        src={`${process.env.PUBLIC_URL || ''}/logo.svg`}
        alt="Motore Gare DS - DS Consulting"
        className="h-20 md:h-28 w-auto mx-auto"
      />
    </div>

    <h1
      className="font-display text-2xl md:text-3xl font-medium mb-3 mx-auto"
      style={{ color: '#0F1B2D', maxWidth: '44rem', textWrap: 'balance' }}
    >
      Benvenuto in Motore Gare DS, il portale di ricerca gare d’appalto di DS Consulting.
    </h1>

    <p
      className="mb-6 mx-auto"
      style={{ color: '#5A6472', maxWidth: '36rem' }}
    >
      Registrati gratuitamente per accedere a una prova gratuita del servizio
    </p>
  </div>

        {/* Card */}
        <div className="max-w-md w-full mx-auto">
        <div className="bg-white rounded-2xl shadow-2xl p-8 relative z-10">
          {/* Banner Conferma Email */}
          {showConfirmationBanner && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Email di conferma inviata alla mail {confirmationEmail}</strong>
                <br />
                <span className="text-xs">Controlla la tua casella email (anche la cartella Spam o Posta indesiderata) e clicca sul link ricevuto per confermare e procedere.</span>
              </p>
              <button
                onClick={() => setShowConfirmationBanner(false)}
                className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline"
              >
                Chiudi
              </button>
            </div>
          )}

          {/* Toggle Login/Register */}
          <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
            <button
              type="button"
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                isLogin
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Login
            </button>
            <button
              type="button"
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                !isLogin
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Registrati
            </button>
          </div>

          {/* LOGIN FORM */}
          {isLogin ? (
            <form onSubmit={handleLoginSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={loginData.email}
                    onChange={handleLoginChange}
                    placeholder="Email"
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={loginData.password}
                    onChange={handleLoginChange}
                    placeholder="Password"
                    required
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Password dimenticata */}
              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  Hai dimenticato la password?
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-primary-600 to-secondary-600 text-white py-3 rounded-lg font-medium hover:from-primary-700 hover:to-secondary-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {isLoading ? 'Accesso in corso...' : 'Accedi'}
              </button>
            </form>
          ) : (
            /* REGISTER FORM */
            <form onSubmit={handleRegisterSubmit} className="space-y-4">
              {/* Nome */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    name="firstName"
                    value={registerData.firstName}
                    onChange={handleRegisterChange}
                    placeholder="Mario"
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Cognome */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cognome *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    name="lastName"
                    value={registerData.lastName}
                    onChange={handleRegisterChange}
                    placeholder="Rossi"
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Nome Azienda */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome Azienda (opzionale)
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    name="companyName"
                    value={registerData.companyName}
                    onChange={handleRegisterChange}
                    placeholder="Azienda SRL"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Telefono Mobile */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefono Mobile *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="tel"
                    name="mobilePhone"
                    value={registerData.mobilePhone}
                    onChange={handleRegisterChange}
                    placeholder="+39 333 123 4567"
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Associazione DS Consulting (scelta multipla) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  DS Consulting *
                </label>
                <div className="space-y-2">
                  <label className="flex items-start gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="federappaltiMembership"
                      value="associato"
                      checked={registerData.federappaltiMembership === 'associato'}
                      onChange={handleRegisterChange}
                      required
                      className="mt-1"
                    />
                    <span>a) Sono associato a DS Consulting</span>
                  </label>

                  <label className="flex items-start gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="federappaltiMembership"
                      value="non_interessato"
                      checked={registerData.federappaltiMembership === 'non_interessato'}
                      onChange={handleRegisterChange}
                      className="mt-1"
                    />
                    <span>b) Non sono associato a DS Consulting e non mi interessa associarmi</span>
                  </label>

                  <label className="flex items-start gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="federappaltiMembership"
                      value="interessato_info"
                      checked={registerData.federappaltiMembership === 'interessato_info'}
                      onChange={handleRegisterChange}
                      className="mt-1"
                    />
                    <span>c) Non sono associato a DS Consulting ma sono potenzialmente interessato ad associarmi e voglio ricevere maggiori informazioni</span>
                  </label>
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={registerData.email}
                    onChange={handleRegisterChange}
                    placeholder="Email"
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={registerData.password}
                    onChange={handleRegisterChange}
                    placeholder="Password"
                    required
                    minLength={8}
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Conferma Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Conferma Password *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={registerData.confirmPassword}
                    onChange={handleRegisterChange}
                    placeholder="Ripeti la password"
                    required
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Checkbox Termini */}
              <div className="flex items-start">
                <button
                  type="button"
                  onClick={() => setRegisterData({...registerData, acceptedTerms: !registerData.acceptedTerms})}
                  className="flex-shrink-0 mt-1"
                >
                  {registerData.acceptedTerms ? (
                    <CheckSquare className="h-5 w-5 text-primary-600" />
                  ) : (
                    <Square className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                <label className="ml-2 text-sm text-gray-600">
                  Accetto i{' '}
                  <a
                    href="/termini"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-600 hover:underline"
                  >
                    Termini e Condizioni
                  </a>{' '}
                  *
                </label>
              </div>

              {/* Checkbox Privacy */}
              <div className="flex items-start">
                <button
                  type="button"
                  onClick={() => setRegisterData({...registerData, acceptedPrivacy: !registerData.acceptedPrivacy})}
                  className="flex-shrink-0 mt-1"
                >
                  {registerData.acceptedPrivacy ? (
                    <CheckSquare className="h-5 w-5 text-primary-600" />
                  ) : (
                    <Square className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                <label className="ml-2 text-sm text-gray-600">
                  Accetto la{' '}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-600 hover:underline"
                  >
                    Privacy Policy
                  </a>{' '}
                  *
                </label>
              </div>

              {/* Checkbox Newsletter (opzionale) */}
              <div className="flex items-start">
                <button
                  type="button"
                  onClick={() => setRegisterData({...registerData, newsletterOptIn: !registerData.newsletterOptIn})}
                  className="flex-shrink-0 mt-1"
                >
                  {registerData.newsletterOptIn ? (
                    <CheckSquare className="h-5 w-5 text-primary-600" />
                  ) : (
                    <Square className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                <label className="ml-2 text-sm text-gray-600">
                  Iscrivimi alla Newsletter DS Consulting
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-primary-600 to-secondary-600 text-white py-3 rounded-lg font-medium hover:from-primary-700 hover:to-secondary-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {isLoading ? 'Registrazione in corso...' : 'Registrati'}
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                Registrandoti riceverai automaticamente il ruolo <strong>Guest</strong>.
                <br />
                Un amministratore potrà successivamente modificare i tuoi permessi.
              </p>
            </form>
          )}
        </div>

        {/* Footer */}
      </div>
    </div>
    </div>
  );
};

export default LoginPage;
